import React, { useState } from 'react';
import { Link } from 'react-scroll';
import { ArrowRight, Sparkles, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import BookingModal from './BookingModal';
import { useTranslation } from 'react-i18next';

const Hero = () => {
  const { t } = useTranslation();
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background with soft gradients */}
      <div className="absolute inset-0 bg-gradient-to-br from-pink-50 via-white to-purple-50 -z-10"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-pink-200/30 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl -z-10"></div>
      <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-green-200/20 rounded-full blur-3xl -z-10"></div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 rounded-full">
              <Sparkles className="w-4 h-4 text-pink-600" />
              <span className="text-sm font-medium text-pink-700">
                {t('hero.badge')}
              </span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
              <span className="text-[#E91E8C]">{t('hero.title')}</span>
            </h1>
            
            <p className="text-xl md:text-2xl font-semibold text-gray-700">
              {t('hero.tagline')}
            </p>
            
            <p className="text-lg text-gray-600 leading-relaxed">
              {t('hero.description')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                size="lg" 
                onClick={() => setIsBookingOpen(true)}
                className="bg-[#E91E8C] hover:bg-[#d01a7a] text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 w-full sm:w-auto"
              >
                <Calendar className="mr-2 w-5 h-5" />
                {t('hero.ctaPrimary')}
              </Button>
              <Link to="contact" smooth={true} duration={500} offset={-80}>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-[#E91E8C] text-[#E91E8C] hover:bg-pink-50 px-8 py-6 text-lg rounded-full shadow-md hover:shadow-lg transition-all w-full sm:w-auto"
                >
                  {t('hero.ctaSecondary')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-pink-200">
              <div className="text-center">
                <p className="text-3xl font-bold text-[#E91E8C]">{t('hero.trustAge')}</p>
                <p className="text-sm text-gray-600 mt-1">{t('hero.trustAgeLabel')}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600">{t('hero.trustRatio')}</p>
                <p className="text-sm text-gray-600 mt-1">{t('hero.trustRatioLabel')}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">{t('hero.trustLanguages')}</p>
                <p className="text-sm text-gray-600 mt-1">{t('hero.trustLanguagesLabel')}</p>
              </div>
            </div>
          </div>

          {/* Right Content - Images */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              {/* Main large image */}
              <div className="col-span-2 rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/putj1ui1_20250411_105420-1.jpg"
                  alt="Giardino esterno dall'alto con piscine e giochi"
                  className="w-full h-80 object-cover"
                />
              </div>
              
              {/* Two smaller images */}
              <div className="rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 transition-transform duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/fbuitp8k_1000089476.jpg"
                  alt="Arte e creatività - Pittura con i piedi"
                  className="w-full h-48 object-cover"
                />
              </div>
              
              <div className="rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 transition-transform duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/4z6vnngz_1000087933.jpg"
                  alt="Apprendimento Montessori - Forme geometriche"
                  className="w-full h-48 object-cover"
                />
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 transform hover:scale-105 transition-transform">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-pink-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">20+</p>
                  <p className="text-sm text-gray-600">Anni di esperienza</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </section>
  );
};

export default Hero;
